import { useState, useEffect } from 'react';

export default function ExitIntentPopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    smsConsent: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const hasShown = sessionStorage.getItem('exitIntentShown');
    if (hasShown) return;

    let inactivityTimer: ReturnType<typeof setTimeout>;

    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0 && !isVisible) {
        setIsVisible(true);
        sessionStorage.setItem('exitIntentShown', 'true');
      }
    };

    const resetInactivityTimer = () => {
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(() => {
        if (!isVisible) {
          setIsVisible(true);
          sessionStorage.setItem('exitIntentShown', 'true');
        }
      }, 30000);
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mousemove', resetInactivityTimer);
    document.addEventListener('keydown', resetInactivityTimer);
    document.addEventListener('scroll', resetInactivityTimer);
    document.addEventListener('touchstart', resetInactivityTimer);
    resetInactivityTimer();

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsVisible(false);
    };
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mousemove', resetInactivityTimer);
      document.removeEventListener('keydown', resetInactivityTimer);
      document.removeEventListener('scroll', resetInactivityTimer);
      document.removeEventListener('touchstart', resetInactivityTimer);
      document.removeEventListener('keydown', handleEscape);
      clearTimeout(inactivityTimer);
    };
  }, [isVisible]);

  const handleClose = () => setIsVisible(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.smsConsent) newErrors.smsConsent = 'SMS consent is required to receive your quote';
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setIsSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('phone', formData.phone);
      body.append('sms_consent', formData.smsConsent ? 'Yes - consented to SMS' : 'No');
      body.append('source', 'Exit Intent Popup');

      const response = await fetch('https://readdy.ai/api/form/d6rk5v3a90d3mf3eh030', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (response.ok) {
        setIsSubmitted(true);
        setTimeout(() => setIsVisible(false), 3500);
      }
    } catch (err) {
      console.error('Exit intent form error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/65 px-4"
      style={{ animation: 'fadeIn 0.2s ease' }}
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative overflow-hidden"
        style={{ animation: 'slideUp 0.3s ease' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Top accent bar */}
        <div className="h-1.5 w-full bg-gradient-to-r from-[#1A3A52] via-[#D97642] to-[#1A3A52]"></div>

        {/* Close */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
          aria-label="Close popup"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        <div className="px-8 pt-7 pb-8">
          {!isSubmitted ? (
            <>
              {/* Icon + Headline */}
              <div className="flex items-start gap-4 mb-5">
                <div className="w-12 h-12 flex items-center justify-center bg-[#D97642]/10 rounded-xl flex-shrink-0">
                  <i className="ri-hand-heart-line text-2xl text-[#D97642]"></i>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 leading-tight">
                    Wait — Before You Go!
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Get a free quote in under 60 seconds. No obligation.
                  </p>
                </div>
              </div>

              {/* Trust badges */}
              <div className="flex gap-3 mb-6">
                {[
                  { icon: 'ri-shield-check-line', label: 'Licensed Agent' },
                  { icon: 'ri-lock-line', label: 'Secure & Private' },
                  { icon: 'ri-time-line', label: '60-Second Quote' },
                ].map(b => (
                  <div key={b.label} className="flex-1 flex flex-col items-center gap-1 bg-gray-50 rounded-lg py-2.5 px-1">
                    <i className={`${b.icon} text-[#D97642] text-base`}></i>
                    <span className="text-[10px] font-semibold text-gray-600 text-center leading-tight">{b.label}</span>
                  </div>
                ))}
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} data-readdy-form id="exit-intent-form" noValidate>
                <div className="space-y-3">
                  {/* Name */}
                  <div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Full Name"
                      className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#D97642]/30 focus:border-[#D97642] transition-colors ${errors.name ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50'}`}
                    />
                    {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                  </div>

                  {/* Email */}
                  <div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Email Address"
                      className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#D97642]/30 focus:border-[#D97642] transition-colors ${errors.email ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50'}`}
                    />
                    {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                  </div>

                  {/* Phone */}
                  <div>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Phone Number"
                      className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#D97642]/30 focus:border-[#D97642] transition-colors ${errors.phone ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50'}`}
                    />
                    {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
                  </div>

                  {/* SMS Consent */}
                  <div className={`rounded-lg p-3 border ${errors.smsConsent ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'}`}>
                    <label className="flex items-start gap-2.5 cursor-pointer">
                      <input
                        type="checkbox"
                        name="smsConsent"
                        checked={formData.smsConsent}
                        onChange={handleChange}
                        className="mt-0.5 w-4 h-4 accent-[#D97642] flex-shrink-0 cursor-pointer"
                      />
                      <span className="text-[11px] text-gray-600 leading-relaxed">
                        I consent to receive automated SMS/text messages from Hoosier Life Insurance regarding my quote, appointment reminders, and insurance updates. <strong>Consent is not required to purchase.</strong> Msg &amp; data rates may apply. Reply <strong>STOP</strong> to opt out.
                      </span>
                    </label>
                    {errors.smsConsent && <p className="text-xs text-red-500 mt-1.5">{errors.smsConsent}</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#D97642] text-white py-3 rounded-lg font-bold text-sm hover:bg-[#C26638] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="ri-shield-check-line"></i>
                        Get My Free Quote
                      </>
                    )}
                  </button>
                </div>
              </form>

              <p className="text-[10px] text-gray-400 text-center mt-3">
                No spam. Your information is secure and never sold.
              </p>
            </>
          ) : (
            <div className="text-center py-6">
              <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                <i className="ri-check-double-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">You're All Set!</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                We've received your request. A licensed Indianapolis agent will reach out shortly with your personalized quote.
              </p>
              <div className="mt-5 flex items-center justify-center gap-2 text-xs text-gray-400">
                <i className="ri-phone-line text-[#D97642]"></i>
                Or call us now: <a href="tel:+13176482983" className="text-[#D97642] font-semibold">(317) 648-2983</a>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}
